import moment from "moment";

import { MESSAGE_ERROR, replaceAttr } from "~utils/constants/message-const";
import { URL_ERROR } from "~utils/constants/api-const";
import { SESSION_NAME } from "~utils/constants/attributes-const";
import { Modal } from "antd";
import { navigate } from "gatsby";
import { REGEX } from "~utils/constants/common-const";

export * as API from "./api-utils";
export * as Dialog from "./dialog-utils";
export * as Obj from "./object-utils";
export * as Date from "./date-utils";
export * as ConstAPI from "./constants/api-const";

function toHalfWidth(value: number | string) {
  const v = value.toString();
  // 半角変換
  return v.replace(/[！-～]/g, function (tmpStr) {
    // 文字コードをシフト
    return String.fromCharCode(tmpStr.charCodeAt(0) - 0xfee0);
  });
}

export function removeComma(n: number | string) {
  if (!n) {
    return n as string;
  }
  // 全角から半角へ変換し、既にカンマが入力されていたら事前に削除
  const n_ = toHalfWidth(n).replace(/,/g, "").trim();
  // 数値でなければ入力値を返却
  if (!/^[+|-]?(\d*)(\.\d+)?$/.test(n_)) {
    return n.toString();
  }
  return n_;
}

export function addComma(n: number | string) {
  if (!n) {
    return n;
  }
  const n_ = removeComma(n);
  // 数値でなければ入力値を返却
  if (!/^[+|-]?(\d*)(\.\d+)?$/.test(n_)) {
    return n;
  }
  // 整数部分と小数部分に分割
  const numData = n_.toString().split(".");
  // 整数部分を3桁カンマ区切りへ
  numData[0] = Number(numData[0])
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  // 小数部分と結合して返却
  return numData.join(".");
}

export function sliceDataOnPage<T>(data: T[], page: number, numOnPage: number) {
  const a =
    window.performance.timing.domContentLoadedEventEnd -
    window.performance.timing.navigationStart;
  if (numOnPage < 0) {
    // 全件
    return data;
  } else {
    const startIdx = numOnPage * (page - 1);
    const endIdx = startIdx + numOnPage;
    return data.slice(startIdx, endIdx);
  }
}

export function select(elm: HTMLInputElement) {
  const userAgent = window.navigator.userAgent.toUpperCase();
  if (userAgent.indexOf("MSIE") === -1 && userAgent.indexOf("TRIDENT") === -1) {
    // IE以外のブラウザの場合
    elm.select();
  } else {
    // IEの場合
    setTimeout(() => {
      elm.select();
    }, 100);
  }
}

export function getTimeLoadDom() {
  return (
    window.performance.timing.domContentLoadedEventEnd -
    window.performance.timing.navigationStart
  );
}

export const sortDate = (dateA, dateB) => {
  const momentA = moment(dateA);
  const momentB = moment(dateB);

  if (momentA.isBefore(momentB)) return -1;
  if (momentA.isAfter(momentB)) return 1;
  return 0;
};

/**
 * Get byte
 *
 * @param str
 */
export function getByte(str: string): number {
  let result = 0;
  for (let i = 0; i < str.length; i++) {
    const chr = str.charCodeAt(i);
    if (
      (chr >= 0x00 && chr < 0x81) ||
      chr === 0xf8f0 ||
      (chr >= 0xff61 && chr < 0xffa0) ||
      (chr >= 0xf8f1 && chr < 0xf8f4)
    ) {
      result += 1;
    } else {
      result += 2;
    }
  }
  return result;
}

/**
 * validate byte length
 *
 * @param label
 * @param value
 * @param max
 */
export function validateByteLength(
  label: string,
  value: string,
  max: number,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const fullSize = max / 2;
    if (getByte(value) > max) {
      reject(
        new Error(
          replaceAttr(MESSAGE_ERROR.E0020, {
            attr: label,
            num1: fullSize.toString(),
            num2: max.toString(),
          }),
        ),
      );
    } else {
      resolve();
    }
  });
}

/**
 * format number
 *
 * @param value
 */
export const formatNumber = (value) => {
  return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/**
 * compare date from > date to
 *
 * @param dateFrom
 * @param dateTo
 */
export const compareDate = (dateFrom: string, dateTo: string) => {
  return new Date(dateFrom) > new Date(dateTo);
};

/**
 * get url redirect error
 *
 * @param value
 * @param urlCurrent
 */
export const getUrlRedirectError = (value, urlCurrent) => {
  if (value === 403 || value === 401) {
    let formatUrl = urlCurrent.replace("/web.d", "");
    if (urlCurrent.includes("/user/")) {
      if (value == 401) {
        sessionStorage.setItem(SESSION_NAME.USER_URL_REDIRECT, formatUrl);
      }
      return replaceAttr(URL_ERROR[value], { modules: "/user" });
    } else {
      if (value == 401) {
        sessionStorage.setItem(SESSION_NAME.ADMIN_URL_REDIRECT, formatUrl);
      }
      return replaceAttr(URL_ERROR[value], { modules: "/admin" });
    }
  }
  if (URL_ERROR[value] == undefined) {
    return "";
  }
  return URL_ERROR[value];
};
/**
 * handle error when call api
 *
 * @param messageErr
 * @param code
 * @param urlCurrent
 */
export const handleErrorCallAPI = async (
  messageErr: string,
  code: number,
  urlCurrent: string,
) => {
  let formatUrl = urlCurrent.replace("/web.d", "");
  let isModuleUser = urlCurrent.includes("/user/");
  if (code === 401) {
    // modules user
    if (isModuleUser) {
      // set old url in session
      sessionStorage.setItem(SESSION_NAME.USER_URL_REDIRECT, formatUrl);
      await navigate(replaceAttr(URL_ERROR[code], { modules: "/user" }));
    } else {
      // set old url in session
      sessionStorage.setItem(SESSION_NAME.ADMIN_URL_REDIRECT, formatUrl);
      await navigate(replaceAttr(URL_ERROR[code], { modules: "/admin" }));
    }
  }
  if (code === 403) {
    // modules user
    if (isModuleUser) {
      //remove old url in session
      sessionStorage.removeItem(SESSION_NAME.USER_URL_REDIRECT);
      Modal.error({
        title: "エラー",
        okText: "確認",
        content: messageErr,
        centered: true,
        onOk: async () => {
          removeOtherModals();
          await navigate(replaceAttr(URL_ERROR[code], { modules: "/user" }));
        },
      });
    } else {
      //remove old url in session
      sessionStorage.removeItem(SESSION_NAME.ADMIN_URL_REDIRECT);
      Modal.error({
        title: "エラー",
        okText: "確認",
        content: messageErr,
        centered: true,
        onOk: async () => {
          removeOtherModals();
          await navigate(replaceAttr(URL_ERROR[code], { modules: "/admin" }));
        },
      });
    }
  }
};

/**
   * format bytes
   *
   * @param bytes
   **/
export const formatBytes = (bytes: number) => {
  const units = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const exp = Math.floor(Math.log(bytes) / Math.log(1024));
  const convertedBytes = bytes / Math.pow(1024, exp);

  return `${convertedBytes.toFixed(2)} ${units[exp]}`;
};


/**
 * Convert keys to snack case
 *
 * @param obj
 */
export function convertKeysToSnakeCase(obj: { [key: string]: any }): {
  [key: string]: any;
} {
  const newObj: { [key: string]: any } = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const snakeCaseKey = key.replace(
        /[A-Z]/g,
        (letter) => `_${letter.toLowerCase()}`,
      );
      newObj[snakeCaseKey] = obj[key];
    }
  }
  return newObj;
}

/**
 * Convert snake case to keys
 *
 * @param obj
 */
export function convertSnakeCaseToKeys(obj: { [key: string]: any }): {
  [key: string]: any;
} {
  const newObj: { [key: string]: any } = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const camelCaseKey = key.replace(/_([a-z])/g, (match, letter) =>
        letter.toUpperCase(),
      );
      newObj[camelCaseKey] = obj[key];
    }
  }
  return newObj;
}

/**
 * Create download, using for export
 * @param contentDisposition reponse header
 * @param blob file buffer
 * @returns
 */
export const createDownload = (contentDisposition: string, blob: Blob) => {
  let fileName = ''
    if (
      contentDisposition.indexOf("filename*=utf-8") !== -1 ||
      contentDisposition.indexOf("filename*=UTF-8") !== -1
    ) {
      fileName = contentDisposition.substring(
        contentDisposition.indexOf("''") + 2,
        contentDisposition.length
      );
      fileName = decodeURI(fileName).replace(/\+/g, " ");
      return fileName;
    } else {
      const startIndex = contentDisposition.indexOf('filename="') + 10;
      const endIndex = contentDisposition.length - 1;
      fileName = contentDisposition.substring(startIndex, endIndex);
    }
    saveAs(blob, fileName);
}

export const validateFormatPhoneNumber = (phoneNumber: string) => {
  const convertedPhoneNumber = phoneNumber.replace(/-/g, "");
  if (convertedPhoneNumber.length < 10 || convertedPhoneNumber.length > 11) {
    return false;
  }

  return REGEX.phone_number.test(phoneNumber);
}

/**
 * get url current session
 * @param path
 */
export const getUrlCurrentSession = (path: string) => {
  let formatPath = path.replace("/web.d", "");
  let isModuleUser = formatPath.includes("/user/");
  let urlCurrentSession: string | null;
  if(isModuleUser){
    urlCurrentSession = sessionStorage.getItem(SESSION_NAME.USER_URL_REDIRECT);
  } else {
    urlCurrentSession = sessionStorage.getItem(SESSION_NAME.ADMIN_URL_REDIRECT);
  }
  return urlCurrentSession;
}

export const removeOtherModals = () => {
  const modals = document.getElementsByClassName("ant-modal-root");
  Array.from(modals).forEach((modal) => {
    modal.remove();
  });
}

export const updateLocalStorage = (key, value) => {
  localStorage.setItem(key, value);

  const event = new CustomEvent('localStorageChange', {
    detail: { key: key, value: value }
  });
  window.dispatchEvent(event);
}